/**
 * TrackingHealthCheck Component
 * 
 * Shows the current health status of tracking settings.
 * Helps users identify what might be preventing reliable tracking.
 */

import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useGeolocation } from '../../hooks/useGeolocation';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Settings,
  Navigation,
  Battery,
  Bell,
  Shield,
  RefreshCw,
  ChevronRight,
  Smartphone,
  HelpCircle
} from 'lucide-react';
import { TrackingSetupWizard } from './TrackingSetupWizard';

const isNative = Capacitor.isNativePlatform();
const isIOS = Capacitor.getPlatform() === 'ios';
const isAndroid = Capacitor.getPlatform() === 'android';

export function TrackingHealthCheck({ compact = false }) {
  const { isInitialized, isTracking, error } = useGeolocation();
  const [showWizard, setShowWizard] = useState(false);
  const [healthStatus, setHealthStatus] = useState({
    location: 'unknown',
    battery: 'unknown',
    notifications: 'unknown',
    overall: 'unknown'
  });
  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    // Check if setup was previously completed
    const setupDone = localStorage.getItem('servio_tracking_setup_complete') === 'true';
    setSetupComplete(setupDone);

    // Determine health status based on available info
    if (isInitialized) {
      const newStatus = {
        location: error?.type === 'permission' ? 'error' : isTracking ? 'good' : 'warning',
        battery: 'unknown', // Can't check directly without native plugin
        notifications: 'unknown',
        overall: 'unknown'
      };

      // Calculate overall status
      if (newStatus.location === 'error') {
        newStatus.overall = 'error';
      } else if (newStatus.location === 'good' && setupComplete) {
        newStatus.overall = 'good';
      } else {
        newStatus.overall = 'warning';
      }

      setHealthStatus(newStatus);
    }
  }, [isInitialized, isTracking, error, setupComplete]);

  // Don't render on web
  if (!isNative) {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <HelpCircle className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'good':
        return 'Good';
      case 'warning':
        return 'Check Settings';
      case 'error':
        return 'Action Required';
      default:
        return 'Unknown';
    }
  };

  const getOverallMessage = () => {
    if (healthStatus.overall === 'good') {
      return 'All systems ready for automatic tracking';
    } else if (healthStatus.overall === 'error') {
      return 'Location permission required for tracking';
    } else if (!setupComplete) {
      return 'Complete setup for reliable tracking';
    } else {
      return 'Some settings may need attention';
    }
  };

  // Compact version for inline display
  if (compact) {
    return (
      <>
        <button
          onClick={() => setShowWizard(true)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            healthStatus.overall === 'good'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : healthStatus.overall === 'error'
              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
          }`}
        >
          {getStatusIcon(healthStatus.overall)}
          <span>Tracking {getStatusLabel(healthStatus.overall)}</span>
        </button>
        
        <TrackingSetupWizard 
          open={showWizard} 
          onClose={() => setShowWizard(false)}
          onComplete={() => setSetupComplete(true)}
        />
      </>
    );
  }

  // Full card version
  return (
    <>
      <Card className={`border ${
        healthStatus.overall === 'good'
          ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
          : healthStatus.overall === 'error'
          ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
          : 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
      }`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                healthStatus.overall === 'good'
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : healthStatus.overall === 'error'
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : 'bg-amber-100 dark:bg-amber-900/30'
              }`}>
                {healthStatus.overall === 'good' ? (
                  <Shield className="h-5 w-5 text-green-600" />
                ) : healthStatus.overall === 'error' ? (
                  <XCircle className="h-5 w-5 text-red-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-slate-900 dark:text-white text-sm">
                  Tracking Health
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {getOverallMessage()}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowWizard(true)}
              className="shrink-0"
            >
              <Settings className="h-4 w-4 mr-1" />
              {setupComplete ? 'Review' : 'Setup'}
            </Button>
          </div>

          {/* Status items */}
          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 grid grid-cols-3 gap-2">
            <div className="flex items-center gap-1.5 text-xs">
              {getStatusIcon(healthStatus.location)}
              <span className="text-slate-600 dark:text-slate-400">Location</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              {getStatusIcon(healthStatus.battery)}
              <span className="text-slate-600 dark:text-slate-400">Battery</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              {getStatusIcon(healthStatus.notifications)}
              <span className="text-slate-600 dark:text-slate-400">Alerts</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <TrackingSetupWizard 
        open={showWizard} 
        onClose={() => setShowWizard(false)}
        onComplete={() => setSetupComplete(true)}
      />
    </>
  );
}

export default TrackingHealthCheck;
